import type { ClientBuilder } from "api-types/builders/clientBuilderTypes";
import type { ClientLocation } from "api-types/locations/locationsResponses";
import type { BuilderBrochureSettings } from "api-types/modelTypes";
import type { LocationWithHeirarchy } from "lib/components/builders/communities/utils/locationUtils";
import { defaultBrochureSettings, mergeBrochureSettingsWithDefaults } from "lib/constants/brochureSettings";
import { shouldDisplayBrochurePricing } from "lib/house/pdf/brochure/utils";

// Re-export from constants for backwards compatibility
export { defaultBrochureSettings, mergeBrochureSettingsWithDefaults };

interface AugmentBrochureSettingsOptions {
  builder?: ClientBuilder;
  location?: ClientLocation;
  locationHierarchy?: LocationWithHeirarchy | null;
  hierarchyLoading?: boolean;
  brochureSettings?: Partial<BuilderBrochureSettings>;
  isPublic?: boolean;
}

export function augmentBrochureSettings(options?: AugmentBrochureSettingsOptions): BuilderBrochureSettings {
  if (!options) {
    return defaultBrochureSettings;
  }

  const { builder, location, locationHierarchy, hierarchyLoading, brochureSettings, isPublic = false } = options;

  let displayOptionPrices;
  // Conservative default during loading
  if (hierarchyLoading) {
    displayOptionPrices = false;
  } else {
    // When done loading, calculate the proper setting
    displayOptionPrices = shouldDisplayBrochurePricing(isPublic, location, locationHierarchy?.ancestors, builder);
  }

  const website =
    brochureSettings?.website ||
    location?.brochureSettings?.website ||
    builder?.brochureSettings?.website ||
    builder?.website ||
    "";
  const logoUrl =
    brochureSettings?.logoUrl ||
    location?.brochureSettings?.logoUrl ||
    builder?.brochureSettings?.logoUrl ||
    builder?.logoUrl ||
    "";

  return {
    ...defaultBrochureSettings,
    ...builder?.brochureSettings,
    ...location?.brochureSettings,
    ...brochureSettings,
    displayOptionPrices,
    website,
    logoUrl,
  };
}

export type BuilderBrochureDisplaySettings = Pick<
  BuilderBrochureSettings,
  | "displayAllOptions"
  | "displayOptionDescriptions"
  | "displayElevationPage"
  | "displayFairHousingLogo"
  | "addElectricalPlans"
  | "displayOptionPrices"
  | "displayDefaultExteriorPaletteWhenSelected"
  | "displayElectricalOptions"
  | "groupOptionsBySalesCategory"
  | "singlePlanPerPage"
  | "displaySqft"
  | "displayPlanLabelDims"
>;

export type BuilderBrochureOptionSettings = Pick<
  BuilderBrochureSettings,
  "displayOptionPrices" | "displayOptionDescriptions"
>;

export type BuilderBrochurePlanSettings = Pick<
  BuilderBrochureSettings,
  "displaySqft" | "displayPlanLabelDims" | "displayElectricalOptions" | "addElectricalPlans" | "singlePlanPerPage"
>;

export const brochureVisibilitySettings: {
  [key in keyof Omit<BuilderBrochureDisplaySettings, "displayOptionPrices">]: { label: string; description: string };
} = {
  displayElevationPage: {
    label: "Add Elevations Page",
    description: "Include an additional page with elevation drawings of the configured home",
  },
  displayAllOptions: {
    label: "Add Option Page",
    description: "Include an additional page with a full list of configured options",
  },
  displayOptionDescriptions: {
    label: "Option Descriptions",
    description: "If Options Page is enabled, display the description of each configured option",
  },
  groupOptionsBySalesCategory: {
    label: "Group Options by Sales Category",
    description: "When the Options page is enabled, group options under sales category section headers.",
  },
  singlePlanPerPage: {
    label: "Single Plan Per Page",
    description: "Maximize the size of the floor plan by displaying only one plan per page",
  },
  displayElectricalOptions: {
    label: "Electrical Content on Floor Plans",
    description: "Display electrical elements on the floor plan(s)",
  },
  displayDefaultExteriorPaletteWhenSelected: {
    label: "Display Default Exterior Palette When Selected",
    description: "Display the default exterior palette for the plan when selected",
  },
  addElectricalPlans: {
    label: "Add Electrical Plan Page(s)",
    description: "Include an additional plan for each floor with electrical content",
  },
  displayFairHousingLogo: {
    label: "Display EHO logo",
    description: "Display the Equal Housing Opportunity logo on in the footer of the brochure",
  },
  displaySqft: {
    label: "Display Square Footage",
    description: "Display the total square footage for the configured home plan",
  },
  displayPlanLabelDims: {
    label: "Display Plan Label Dimensions",
    description: "Display the dimensions labels for each room on the floor plan",
  },
};
